import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SharedWithMeResource {
  id: string;
  type: 'caso' | 'chat';
  title: string;
  resourceId: string;
  sharedBy: {
    id: string;
    name: string;
    email: string;
  };
  sharedAt: Date;
  expiresAt?: Date | null;
  permissionLevel: string;
}

export function useSharedWithMe() {
  return useQuery({
    queryKey: ['shared-with-me'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return [];

      const resources: SharedWithMeResource[] = [];

      // Buscar casos compartilhados comigo
      const { data: casoShares, error: casoError } = await supabase
        .from('caso_user_shares')
        .select(`
          id,
          caso_id,
          shared_at,
          expires_at,
          permission_level,
          shared_by_user_id
        `)
        .eq('shared_with_user_id', userData.user.id);

      if (casoError) throw casoError;

      // Buscar detalhes dos casos
      for (const share of casoShares || []) {
        const { data: caso } = await supabase
          .from('casos')
          .select('title')
          .eq('id', share.caso_id)
          .single();

        const { data: sharedByUser } = await supabase
          .from('users')
          .select('id, display_name, email')
          .eq('id', share.shared_by_user_id)
          .single();

        if (caso && sharedByUser) {
          resources.push({
            id: share.id,
            type: 'caso',
            title: caso.title,
            resourceId: share.caso_id,
            sharedBy: {
              id: sharedByUser.id,
              name: sharedByUser.display_name || sharedByUser.email,
              email: sharedByUser.email,
            },
            sharedAt: new Date(share.shared_at),
            expiresAt: share.expires_at ? new Date(share.expires_at) : null,
            permissionLevel: share.permission_level,
          });
        }
      }

      // Buscar conversas compartilhadas comigo
      const { data: chatShares, error: chatError } = await supabase
        .from('conversation_user_shares')
        .select(`
          id,
          conversation_id,
          shared_at,
          expires_at,
          permission_level,
          shared_by_user_id
        `)
        .eq('shared_with_user_id', userData.user.id);

      if (chatError) throw chatError;

      // Buscar detalhes das conversas
      for (const share of chatShares || []) {
        const { data: conversation } = await supabase
          .from('legal_conversations')
          .select('title')
          .eq('id', share.conversation_id)
          .is('deleted_at', null)
          .single();

        const { data: sharedByUser } = await supabase
          .from('users')
          .select('id, display_name, email')
          .eq('id', share.shared_by_user_id)
          .single();

        if (conversation && sharedByUser) {
          resources.push({
            id: share.id,
            type: 'chat',
            title: conversation.title,
            resourceId: share.conversation_id,
            sharedBy: {
              id: sharedByUser.id,
              name: sharedByUser.display_name || sharedByUser.email,
              email: sharedByUser.email,
            },
            sharedAt: new Date(share.shared_at),
            expiresAt: share.expires_at ? new Date(share.expires_at) : null,
            permissionLevel: share.permission_level,
          });
        }
      }

      // Ordenar por data mais recente
      return resources.sort((a, b) => b.sharedAt.getTime() - a.sharedAt.getTime());
    },
  });
}
